import React, { useCallback } from "react";
import { classNames } from "../../common_util";
import { Product } from "../types/catalog";
import { ReactComponent as Delete } from "images/icons/delete.svg";
import { ReactComponent as Add } from "images/icons/add.svg";
import { useCart, useModal, useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { NavigationState, serializeShopState, StorefrontPage } from "../types/navigation";
import { CartItem } from "src/storefront/types/cart";
import * as CM from "src/storefront/types/cart";

export const ProductListingGridElement = ({
  product,
  addToCart,
}: {
  product: Product;
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
}): JSX.Element => {
  const config = useStorefrontConfig();
  const showModal = useModal();
  const cart = useCart();

  const { isInFulfillableTimeRange, isOrderable, getPrimaryImageUrl } = useStorefrontHelper();

  const priceDisplay = product.price ? config.priceFormatter.format(product.price / 100.0) : "";

  const countInCart = cart?.getCountOfItemInCart(product) || 0;

  const isAvailable = config.presentationStatus == "open_for_business" && isInFulfillableTimeRange(product) && isOrderable(product);

  const productModalOnClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    showModal(product, true);
  }, [product, showModal])

  const classes = classNames({
    "product-listing-is-grid": true,
    "is-available": isAvailable,
    "is-unavailable": !isAvailable,
  });

  const addItemToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (addToCart)
      if (product.kind == "leaf" || product.kind == "branch") {

        if (product.kind == "branch") { showModal(product, true) }

        if (product.kind == "leaf") { await addToCart(CartItem.fromProduct(product)) }
      }
  };

  const productDetails = <div className="product-details" title={product.title}>
    <p>
      <span>{product.title}</span>
    </p>
  </div>

  const productImage = <div
    className="product-image"
    data-lazy-images-target="image"
    data-lazy-images-imagery-variant="210x210"
    data-lazy-images-imagery-path-value={getPrimaryImageUrl(product)}
  >
    {countInCart > 0 ?
      <div className="badge is-quantity is-round" data-testid="quantity">{countInCart}</div>
    : <></>}
  </div>;

  const productPrice = <div className="price">
    {priceDisplay}
  </div>;

  const quickAddToCartButton = <button role="button" aria-label="Quick add" className="button is-tiny is-icon-only has-plain-icon is-raised" data-testid="quick-add-to-cart" onClick={addItemToCart}><Add className="icon streamline-icon" /></button>;

  const outOfStockBadge = <div className="badge is-strong">
    <Delete className="icon streamline-icon is-visible-above-desktop" /><span>Out<span className="is-visible-above-desktop"> of stock</span></span>
  </div>;

  const fakeLinkHref = `#${serializeShopState(new NavigationState(StorefrontPage.Shop, "", [], product.fullKeyPath()))}`;

  return (
    <a href={fakeLinkHref} className={classes} role="link" data-cy={isAvailable && "menu-item"} onClick={productModalOnClick}>
      {productImage}
      {productDetails}

      <div className="product-footer">
        {isOrderable(product) ? productPrice : outOfStockBadge}
        {isAvailable && isOrderable(product) ? quickAddToCartButton : <></>}
      </div>
    </a>
  );
}
